import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Button,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const PrivacyPolicy = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const lastUpdated = 'May 18, 2026';
  const effectiveDate = 'February 16, 2026';

  const SectionTitle = ({ children }) => (
    <Typography
      variant="h5"
      component="h2"
      sx={{ mt: 4, mb: 2, color: theme.palette.primary.main }}
    >
      {children}
    </Typography>
  );

  const SubSectionTitle = ({ children }) => (
    <Typography
      variant="h6"
      component="h3"
      sx={{ mt: 3, mb: 1.5, color: theme.palette.text.primary }}
    >
      {children}
    </Typography>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>

        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 } }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Privacy Policy
          </Typography>

          <Typography variant="body2" color="text.secondary" paragraph>
            Last Updated: {lastUpdated} | Effective Date: {effectiveDate}
          </Typography>

          <Divider sx={{ my: 3 }} />

          {/* Introduction */}
          <Typography variant="body1" paragraph>
            FamilyForLife ("we," "our," or "us") is committed to protecting your privacy.
            This Privacy Policy explains how we collect, use, disclose, and safeguard your
            information when you use our family management web application and related services
            (collectively, the "Service").
          </Typography>

          <Typography variant="body1" paragraph>
            This policy is designed to comply with the General Data Protection Regulation (GDPR),
            the California Consumer Privacy Act (CCPA), and other applicable data protection laws.
          </Typography>

          {/* Table of Contents */}
          <SectionTitle>Table of Contents</SectionTitle>
          <List dense>
            {[
              'Information We Collect',
              'How We Use Your Information',
              'SMS / Text Message Communications',
              'Legal Basis for Processing (GDPR)',
              'Information Sharing and Disclosure',
              'Data Retention',
              'Your Privacy Rights',
              'CCPA-Specific Rights',
              'International Data Transfers',
              'Data Security',
              'Children\'s Privacy',
              'Cookies and Tracking Technologies',
              'Changes to This Policy',
              'Contact Us',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemText
                  primary={`${index + 1}. ${item}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>

          {/* 1. Information We Collect */}
          <SectionTitle>1. Information We Collect</SectionTitle>

          <SubSectionTitle>1.1 Information You Provide Directly</SubSectionTitle>
          <Typography variant="body1" paragraph>
            We collect information you voluntarily provide when using our Service:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Account Information"
                secondary="Name, email address, password, profile photo, and date of birth"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Family Information"
                secondary="Family relationships, family tree data, emergency contacts, and health updates"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Content You Create"
                secondary="Photos, documents, announcements, events, discussions, and messages"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Communication Data"
                secondary="Support tickets, feedback, and correspondence with us"
              />
            </ListItem>
          </List>

          <SubSectionTitle>1.2 Information Collected Automatically</SubSectionTitle>
          <Typography variant="body1" paragraph>
            When you access our Service, we automatically collect:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Device Information"
                secondary="Device type, operating system, browser type, and unique device identifiers"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Usage Data"
                secondary="Pages visited, features used, time spent on pages, and interaction patterns"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Log Data"
                secondary="IP address, access times, referring URLs, and error logs"
              />
            </ListItem>
          </List>

          <SubSectionTitle>1.3 Mobile Phone Number</SubSectionTitle>
          <Typography variant="body1" paragraph>
            If you provide a mobile phone number during registration or in your account settings,
            we collect and store that number to send you SMS communications you have opted in to receive,
            including one-time passcodes (OTP), account notifications, event reminders, and family
            group updates. Providing a mobile number is optional unless required for two-factor
            authentication.
          </Typography>

          <SubSectionTitle>1.4 Information from Third Parties</SubSectionTitle>
          <Typography variant="body1" paragraph>
            We may receive information from third-party services when you choose to authenticate
            through them (e.g., Google OAuth), including your name, email, and profile photo.
          </Typography>

          {/* 2. How We Use Your Information */}
          <SectionTitle>2. How We Use Your Information</SectionTitle>
          <Typography variant="body1" paragraph>
            We use your information for the following purposes:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Provide and maintain the Service" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Process your registration and manage your account" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Enable family connections and sharing features" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Send service-related notifications and updates, including SMS messages where you have opted in" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Respond to your inquiries and provide customer support" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Improve and optimize our Service" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Detect, prevent, and address security issues" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Comply with legal obligations" />
            </ListItem>
          </List>

          {/* 3. SMS / Text Message Communications */}
          <SectionTitle>3. SMS / Text Message Communications</SectionTitle>
          <Typography variant="body1" paragraph>
            Family For Life App Inc operates an SMS messaging program powered by Twilio to send
            text messages to users who have explicitly opted in. This section describes how we
            handle SMS communications in compliance with the Telephone Consumer Protection Act
            (TCPA) and A2P 10DLC carrier requirements.
          </Typography>

          <SubSectionTitle>3.1 Opt-In Consent</SubSectionTitle>
          <Typography variant="body1" paragraph>
            By creating an account at familyforlife.app and checking the consent box during
            registration, you agree to receive text messages from Family For Life App Inc,
            including verification codes, account notifications, and event reminders. Message
            and data rates may apply. You may also opt in at any time by texting <strong>START</strong> or{' '}
            <strong>JOIN</strong> to our campaign number. Consent is recorded at the time it is given.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Opt-in confirmation message:</strong> "Family For Life App: You are now opted
            in to receive messages including verification codes, notifications, and reminders.
            Msg &amp; data rates may apply. Reply HELP for help or STOP to cancel."
          </Typography>

          <SubSectionTitle>3.2 Types of Messages We Send</SubSectionTitle>
          <List>
            <ListItem><ListItemText primary="One-time passcodes (OTP) for account verification and two-factor authentication" /></ListItem>
            <ListItem><ListItemText primary="Account notifications (e.g. a document has been shared with you)" /></ListItem>
            <ListItem><ListItemText primary="Event reminders and RSVP prompts" /></ListItem>
            <ListItem><ListItemText primary="Family group updates" /></ListItem>
            <ListItem><ListItemText primary="Welcome messages upon account creation" /></ListItem>
            <ListItem><ListItemText primary="Payment and billing reminders" /></ListItem>
          </List>
          <Typography variant="body1" paragraph>
            Message frequency varies based on your activity. Standard message and data rates may apply.
          </Typography>

          <SubSectionTitle>3.3 How to Opt Out</SubSectionTitle>
          <Typography variant="body1" paragraph>
            You may opt out of SMS messages at any time by replying <strong>STOP</strong>,{' '}
            <strong>CANCEL</strong>, <strong>END</strong>, <strong>QUIT</strong>,{' '}
            <strong>UNSUBSCRIBE</strong>, or <strong>REVOKE</strong> to any message we send.
            Upon opting out you will receive one final confirmation: "You have successfully been
            unsubscribed. You will not receive any more messages from this number. Reply START
            to resubscribe." No further messages will be sent after opt-out.
          </Typography>

          <SubSectionTitle>3.4 Help</SubSectionTitle>
          <Typography variant="body1" paragraph>
            Reply <strong>HELP</strong> or <strong>INFO</strong> at any time for assistance.
            You can also contact us at{' '}
            <Link href="mailto:support@familyforlife.app">support@familyforlife.app</Link>.
          </Typography>

          <SubSectionTitle>3.5 SMS Service Provider</SubSectionTitle>
          <Typography variant="body1" paragraph>
            We use Twilio, Inc. to deliver SMS messages. Your mobile number and message content
            are transmitted to Twilio solely for the purpose of delivering messages you have
            requested. Twilio's privacy policy is available at{' '}
            <Link href="https://www.twilio.com/en-us/legal/privacy" target="_blank" rel="noopener noreferrer">
              twilio.com/legal/privacy
            </Link>.
            Mobile information is not shared with third parties or affiliates for marketing
            or promotional purposes.
          </Typography>

          {/* 4. Legal Basis for Processing (GDPR) */}
          <SectionTitle>4. Legal Basis for Processing (GDPR)</SectionTitle>
          <Typography variant="body1" paragraph>
            For users in the European Economic Area (EEA), UK, and Switzerland, we process
            your personal data based on the following legal grounds:
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ my: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Processing Activity</strong></TableCell>
                  <TableCell><strong>Legal Basis</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Account creation and service delivery</TableCell>
                  <TableCell>Contract performance</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Marketing communications</TableCell>
                  <TableCell>Consent</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Security and fraud prevention</TableCell>
                  <TableCell>Legitimate interests</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Service analytics and improvement</TableCell>
                  <TableCell>Legitimate interests</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Legal compliance</TableCell>
                  <TableCell>Legal obligation</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* 5. Information Sharing and Disclosure */}
          <SectionTitle>5. Information Sharing and Disclosure</SectionTitle>
          <Typography variant="body1" paragraph>
            We do not sell your personal information. We may share your information in the
            following circumstances:
          </Typography>

          <SubSectionTitle>5.1 With Your Family Members</SubSectionTitle>
          <Typography variant="body1" paragraph>
            Information you share within your family group is visible to other family members
            based on the privacy settings and permissions you configure.
          </Typography>

          <SubSectionTitle>5.2 Service Providers</SubSectionTitle>
          <Typography variant="body1" paragraph>
            We work with trusted third-party service providers who assist us in operating
            our Service, including:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Firebase (Google Cloud)"
                secondary="Authentication, database, and file storage"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Twilio, Inc."
                secondary="SMS/text message delivery. Mobile numbers are shared with Twilio solely to deliver messages you have opted in to receive. Mobile information is not sold or shared with third parties for marketing purposes."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Email Service Providers"
                secondary="Transactional emails and notifications"
              />
            </ListItem>
          </List>

          <SubSectionTitle>5.3 Legal Requirements</SubSectionTitle>
          <Typography variant="body1" paragraph>
            We may disclose your information if required by law, legal process, or government
            request, or to protect our rights, privacy, safety, or property.
          </Typography>

          {/* 6. Data Retention */}
          <SectionTitle>6. Data Retention</SectionTitle>
          <Typography variant="body1" paragraph>
            We retain your personal data for as long as necessary to provide our Service and
            fulfill the purposes described in this policy. Specifically:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Account data"
                secondary="Retained while your account is active and for 30 days after deletion request"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Content you create"
                secondary="Retained until you delete it or close your account"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Usage logs"
                secondary="Retained for up to 90 days for security and debugging purposes"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Backup data"
                secondary="Retained for up to 180 days after deletion from active systems"
              />
            </ListItem>
          </List>

          {/* 7. Your Privacy Rights */}
          <SectionTitle>7. Your Privacy Rights</SectionTitle>
          <Typography variant="body1" paragraph>
            Depending on your location, you may have the following rights regarding your
            personal data:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Right of Access"
                secondary="Request a copy of the personal data we hold about you"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Right to Rectification"
                secondary="Request correction of inaccurate or incomplete data"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Right to Erasure"
                secondary="Request deletion of your personal data ('right to be forgotten')"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Right to Restrict Processing"
                secondary="Request limitation of how we process your data"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Right to Data Portability"
                secondary="Request transfer of your data to another service"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Right to Object"
                secondary="Object to processing based on legitimate interests"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Right to Withdraw Consent"
                secondary="Withdraw consent where processing is based on consent"
              />
            </ListItem>
          </List>
          <Typography variant="body1" paragraph>
            To exercise these rights, please contact us at{' '}
            <Link href="mailto:privacy@familyforlife.app">privacy@familyforlife.app</Link>.
          </Typography>

          {/* 8. CCPA-Specific Rights */}
          <SectionTitle>8. CCPA-Specific Rights (California Residents)</SectionTitle>
          <Typography variant="body1" paragraph>
            If you are a California resident, you have additional rights under the California
            Consumer Privacy Act (CCPA):
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Right to Know"
                secondary="Request disclosure of categories and specific pieces of personal information collected"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Right to Delete"
                secondary="Request deletion of personal information we collected from you"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Right to Opt-Out"
                secondary="Opt out of the sale of personal information (Note: We do not sell personal information)"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Right to Non-Discrimination"
                secondary="Not receive discriminatory treatment for exercising CCPA rights"
              />
            </ListItem>
          </List>

          <Typography variant="body1" paragraph>
            <strong>Categories of Personal Information Collected (Last 12 Months):</strong>
          </Typography>
          <List dense>
            <ListItem><ListItemText primary="Identifiers (name, email, IP address)" /></ListItem>
            <ListItem><ListItemText primary="Personal information categories under California Civil Code Section 1798.80" /></ListItem>
            <ListItem><ListItemText primary="Internet or network activity information" /></ListItem>
            <ListItem><ListItemText primary="Geolocation data (approximate, based on IP)" /></ListItem>
          </List>

          <Typography variant="body1" paragraph>
            <strong>We do not sell personal information.</strong> To submit a CCPA request,
            contact us at{' '}
            <Link href="mailto:privacy@familyforlife.app">privacy@familyforlife.app</Link>{' '}
            or call us toll-free. We will verify your identity before processing requests.
          </Typography>

          {/* 9. International Data Transfers */}
          <SectionTitle>9. International Data Transfers</SectionTitle>
          <Typography variant="body1" paragraph>
            Your information may be transferred to and processed in countries outside your
            country of residence, including the United States, where data protection laws
            may differ. We implement appropriate safeguards for international transfers, including:
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="Standard Contractual Clauses approved by the European Commission" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Data Processing Agreements with our service providers" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Reliance on adequacy decisions where applicable" />
            </ListItem>
          </List>

          {/* 10. Data Security */}
          <SectionTitle>10. Data Security</SectionTitle>
          <Typography variant="body1" paragraph>
            We implement industry-standard security measures to protect your personal data:
          </Typography>
          <List>
            <ListItem><ListItemText primary="Encryption of data in transit (TLS/SSL) and at rest" /></ListItem>
            <ListItem><ListItemText primary="Secure authentication with Firebase Auth" /></ListItem>
            <ListItem><ListItemText primary="Role-based access controls" /></ListItem>
            <ListItem><ListItemText primary="Regular security audits and monitoring" /></ListItem>
            <ListItem><ListItemText primary="Firestore Security Rules for database access" /></ListItem>
          </List>
          <Typography variant="body1" paragraph>
            While we strive to protect your data, no method of transmission or storage is
            100% secure. If you discover a security vulnerability, please report it to{' '}
            <Link href="mailto:security@familyforlife.app">security@familyforlife.app</Link>.
          </Typography>

          {/* 11. Children's Privacy */}
          <SectionTitle>11. Children's Privacy</SectionTitle>
          <Typography variant="body1" paragraph>
            Our Service is intended for users aged 13 and older. We do not knowingly collect
            personal information from children under 13 (or under 16 in the EEA) without
            verifiable parental consent. If you believe we have collected information from
            a child without proper consent, please contact us immediately.
          </Typography>
          <Typography variant="body1" paragraph>
            Family administrators may add children to their family group with parental
            supervision. Parents or guardians are responsible for managing children's
            accounts and the information shared about them.
          </Typography>

          {/* 12. Cookies and Tracking Technologies */}
          <SectionTitle>12. Cookies and Tracking Technologies</SectionTitle>
          <Typography variant="body1" paragraph>
            We use cookies and similar technologies to enhance your experience. For detailed
            information about the cookies we use and your choices, please see our{' '}
            <Link component={RouterLink} to="/cookies">Cookie Policy</Link>.
          </Typography>

          {/* 13. Changes to This Policy */}
          <SectionTitle>13. Changes to This Policy</SectionTitle>
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time. We will notify you of
            material changes by:
          </Typography>
          <List>
            <ListItem><ListItemText primary="Posting the updated policy with a new 'Last Updated' date" /></ListItem>
            <ListItem><ListItemText primary="Sending you an email notification for significant changes" /></ListItem>
            <ListItem><ListItemText primary="Displaying a prominent notice in the Service" /></ListItem>
          </List>
          <Typography variant="body1" paragraph>
            Your continued use of the Service after changes become effective constitutes
            acceptance of the revised policy.
          </Typography>

          {/* 14. Contact Us */}
          <SectionTitle>14. Contact Us</SectionTitle>
          <Typography variant="body1" paragraph>
            If you have questions about this Privacy Policy or our data practices, please
            contact us:
          </Typography>
          <Box sx={{ pl: 2, borderLeft: `3px solid ${theme.palette.primary.main}`, my: 2 }}>
            <Typography variant="body1">
              <strong>FamilyForLife</strong><br />
              Email: <Link href="mailto:privacy@familyforlife.app">privacy@familyforlife.app</Link><br />
              Data Protection Officer: <Link href="mailto:dpo@familyforlife.app">dpo@familyforlife.app</Link>
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            For GDPR-related inquiries, you may also lodge a complaint with your local
            supervisory authority.
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Link component={RouterLink} to="/terms" color="primary">
              Terms of Service
            </Link>
            <Link component={RouterLink} to="/cookies" color="primary">
              Cookie Policy
            </Link>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
